
import React, { useState, useEffect, useRef } from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { getMenuImageUrl } from '@/services/storage/imageService';
import { useIsMobile } from '@/hooks/use-mobile';
import { useConnectionQuality } from '@/hooks/useConnectionQuality';

interface MenuImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  aspectRatio?: number;
  fallbackText?: string;
  objectFit?: 'cover' | 'contain' | 'fill';
  size?: 'thumbnail' | 'card' | 'full';
  loading?: 'lazy' | 'eager';
  placeholder?: boolean;
}

const MenuImage: React.FC<MenuImageProps> = ({
  src,
  alt,
  className = "",
  aspectRatio = 16 / 9,
  fallbackText,
  objectFit = "cover",
  size = "card",
  loading = "lazy",
  placeholder = true
}) => {
  const [error, setError] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const retryCount = useRef<number>(0);
  const maxRetries = 1;
  const isMobile = useIsMobile();
  const connectionQuality = useConnectionQuality();
  
  useEffect(() => {
    if (!src || src === "null" || src === "undefined" || src === "") {
      setError(true);
      return;
    }
    
    try {
      // Adaptive quality based on connection
      const adaptiveSize = connectionQuality === 'slow' ? 
        (size === 'full' ? 'card' : 'thumbnail') : size;
      
      // Use optimized image URL with automatic format conversion
      const optimizedUrl = getMenuImageUrl(src, adaptiveSize as 'thumbnail' | 'card' | 'full');
      
      if (!optimizedUrl) {
        setError(true);
        return;
      }
      
      setError(false);
      setLoaded(false);
      retryCount.current = 0;
      setImgSrc(optimizedUrl);
      
    } catch (e) {
      console.error(`MenuImage: Error processing image URL: ${src}`, e);
      setError(true);
    }
  }, [src, size]);
  
  const handleError = () => {
    if (loaded) return;
    
    console.error(`MenuImage: Error loading image: ${imgSrc}`);
    
    if (retryCount.current < maxRetries) {
        retryCount.current += 1;
        console.log(`MenuImage: Retry attempt ${retryCount.current}/${maxRetries}`);
        
        setTimeout(() => {
            if (imgSrc && !loaded) {
                setImgSrc(imgSrc + '?retry=' + retryCount.current);
            }
        }, 1000);
        return;
    }
    
    setError(true);
  };
  
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      console.error(`MenuImage: Image loaded but has invalid dimensions: ${imgSrc}`);
      setError(true);
      return;
    }
    
    console.log(`MenuImage: Image loaded successfully: ${imgSrc}`);
    setLoaded(true);
    setError(false);
  };
  
  
  // Tiny placeholder pour éviter layout shift
  if (!imgSrc && placeholder) {
    return (
      <div className={`relative overflow-hidden bg-gray-100 ${className}`} style={{aspectRatio}}>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 text-gray-300">📷</div>
        </div>
      </div>
    );
  }
  
  if (error || !src || src === "null" || src === "undefined" || src === "") {
    return (
      <div className={`relative overflow-hidden bg-gray-200 flex items-center justify-center ${className}`} style={{aspectRatio}}>
        <div className="text-gray-500 p-2 text-center text-sm">
          {fallbackText || alt || "Image non disponible"}
        </div>
      </div>
    );
  }
  
  if (className.includes('h-32') || className.includes('h-48') || className.includes('h-full')) {
    return (
      <div className={`relative overflow-hidden bg-gray-200 ${className}`}>
        {imgSrc && (
          <>
            <img 
              src={imgSrc}
              alt={alt}
              className={`w-full h-full object-${objectFit} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
              onError={handleError}
              onLoad={handleLoad}
              loading={loading}
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: objectFit
              }}
            />
            {!loaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                <span className="text-gray-500 text-sm">Chargement...</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
  
  return (
    <div className={`relative overflow-hidden bg-gray-200 ${className}`}>
      <AspectRatio ratio={aspectRatio}>
        {imgSrc && (
          <>
            <img 
              src={imgSrc}
              alt={alt}
              className={`w-full h-full object-${objectFit} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
              onError={handleError}
              onLoad={handleLoad}
              loading={loading}
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: objectFit
              }}
            />
            {!loaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                <span className="text-gray-500 text-sm">Chargement...</span>
              </div>
            )}
          </>
        )}
      </AspectRatio>
    </div>
  );
};

export default MenuImage;
